import { useCallback, useEffect, useRef, useState } from "react";

/**
 * How long something is given to finish leaving before it is taken out anyway.
 * Nothing in the interface animates for this long; it is the answer to an
 * animation that never runs at all — a stylesheet that is not there, a frame
 * the browser dropped — so that a panel can never be left on screen forever.
 */
const LEAVE_FALLBACK_MS = 1000;

/** Attaches to the element carrying the leaving animation. */
type LeavingRef = (element: HTMLElement | null) => void;

export interface Leaving<T> {
  /** What has just been replaced, until its leaving animation has finished. */
  leaving: T | null;
  ref: LeavingRef;
}

export interface SurfacePresence {
  /** Whether the surface belongs in the tree: it is open, or still leaving. */
  present: boolean;
  /** Whether it is on its way out, which is what the leaving class asks. */
  leaving: boolean;
  ref: LeavingRef;
}

/**
 * Keeps what a state change replaced until it has finished leaving.
 *
 * Rendering the new value the moment the state flips takes the old one's exit
 * with it — the browser has nothing left to animate, which is why a panel that
 * fades in vanishes in a frame. This holds the outgoing value instead, and
 * drops it when the animation says it is done rather than after a duration
 * written down a second time in TypeScript.
 */
export function useLeaving<T>(
  current: T,
  /**
   * The change happened by itself rather than at a press — an answer arriving,
   * not a visitor choosing — so the old value goes without an exit to watch.
   */
  quiet = false,
): Leaving<T> {
  const [leaving, setLeaving] = useState<T | null>(null);
  const previous = useRef(current);
  const elementRef = useRef<HTMLElement | null>(null);

  const ref = useCallback<LeavingRef>((element) => {
    elementRef.current = element;
  }, []);

  useEffect(() => {
    if (previous.current === current) return;
    if (!quiet) setLeaving(previous.current);
    previous.current = current;
  }, [current, quiet]);

  useEffect(() => {
    if (leaving === null) return;
    const element = elementRef.current;
    const done = () => setLeaving(null);
    // An animation on a child of the surface is not the surface leaving.
    const onEnd = (event: AnimationEvent) => {
      if (event.target === element) done();
    };
    element?.addEventListener("animationend", onEnd);
    const fallback = window.setTimeout(done, LEAVE_FALLBACK_MS);
    return () => {
      element?.removeEventListener("animationend", onEnd);
      window.clearTimeout(fallback);
    };
  }, [leaving]);

  return { leaving, ref };
}

/**
 * The same thing for a surface that is simply open or closed: it stays in the
 * tree, marked as leaving, until its exit has run.
 */
export function useSurfacePresence(open: boolean): SurfacePresence {
  const { leaving, ref } = useLeaving(open);
  const isLeaving = !open && leaving === true;
  return { present: open || isLeaving, leaving: isLeaving, ref };
}
